/* eslint-disable */
// ============================================================================
// Stage 2 deadline notifications — read-only derivation hook.
//
// Consumes a designer-scoped dev list (caller's responsibility — App.js
// computes scopedDevsForDeadline before passing down) and groups the
// urgent/overdue devs by stage for the DeadlinePanel.
//
// State is local to the trigger component (AppHeader) per Convention #126
// — this hook has no internal state.
// ============================================================================

import { useMemo } from 'react';
import { splitOverdue } from '../utils/devOverdue';

// Stage display order in the panel — Drawing → Hand → Color, matches the
// prototype deadline-panel-v2.html group order and the rest of the app's
// canonical stage order (STAGES.* in shared.js).
const STAGE_ORDER = ['Drawing stage', 'Hand made sample stage', 'Color sample stage'];

// 3-letter key used by CSS module classes (.iconDraw/.iconHand/.iconColor)
// and the prototype's group-ico variants.
const STAGE_KEY = {
  'Drawing stage':          'draw',
  'Hand made sample stage': 'hand',
  'Color sample stage':     'color',
};

// User-facing short labels (the segmented filter + group titles per prototype).
const STAGE_LABEL = {
  'Drawing stage':          'Drawing',
  'Hand made sample stage': 'Hand Made',
  'Color sample stage':     'Color Sample',
};

/**
 * Groups the overdue devs by stage, through the one rule (utils/devOverdue,
 * AUD-06): past due, not snoozed, not excluded. No amber / "approaching" tier.
 * A dev more than STUCK_AFTER_DAYS past due is NOT on the bell — it goes to
 * `stuck` ("Stuck — needs review" in the panel) and out of totalCount, which
 * is the badge. Caller passes a designer-scoped list.
 *
 * @param {Array} devs — pre-scoped (admin sees all, designer sees own)
 * @returns {{
 *   totalCount: number,
 *   byStage: { draw: Item[], hand: Item[], color: Item[] },
 *   stageOrder: Array<{ key, label, stage }>,
 *   stuck: Item[],
 *   stuckCount: number
 * }}
 * where Item = { dev, daysOver: number, stageKey: 'draw'|'hand'|'color' }, most overdue first
 */
export function useDeadlineNotifications(devs) {
  return useMemo(() => {
    const byStage = { draw: [], hand: [], color: [] };
    const { overdue, stuck: stuckRaw } = splitOverdue(devs);
    const keyOf = (dev) => STAGE_KEY[dev?.stage_status || dev?.data?.stage_status];

    for (const item of overdue) {
      const key = keyOf(item.dev);
      if (key) byStage[key].push({ ...item, stageKey: key }); // unknown stage — defensive skip
    }
    const stuck = stuckRaw.filter((item) => keyOf(item.dev)).map((item) => ({ ...item, stageKey: keyOf(item.dev) }));

    return {
      totalCount: byStage.draw.length + byStage.hand.length + byStage.color.length,
      byStage,
      stageOrder: STAGE_ORDER.map(s => ({ key: STAGE_KEY[s], label: STAGE_LABEL[s], stage: s })),
      stuck,
      stuckCount: stuck.length,
    };
  }, [devs]);
}
